import type Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Icon,
  Loader
} from '@insio/ui-toolkit';
import { BillingModel } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { BillingProvider } from '@/generated/graphql';

interface PlanPrice {
  id: string;
  amount: Decimal;
  currency: string;
  interval: string;
}

export type PlanCardAction =
  | { type: 'currentPlan' }
  | { type: 'cancelScheduledChange'; loading: boolean; onClick: () => void }
  | { type: 'cancelSubscription'; loading: boolean; onClick: () => void }
  | { type: 'checkout'; loading: boolean; onClick: () => void }
  | { type: 'upgrade'; loading: boolean; onClick: () => void }
  | { type: 'scheduleDowngrade'; loading: boolean; onClick: () => void }
  | { type: 'disabled' };

export interface PlanCardProps {
  planId: string;
  name: string;
  description?: string | null;
  billingModel: BillingModel;
  maxDocumentsPerMonth: number;
  /** Credits granted each period; null on a documents plan. */
  monthlyCredits: number | null;
  canExceedLimit: boolean;
  billingProvider: BillingProvider;
  prices: PlanPrice[];
  /** True when this card represents the account's currently active plan. */
  isCurrentPlan: boolean;
  /** Action rendered in the card footer — determined by PaymentsSettings. */
  action: PlanCardAction;
}

export function PlanCard({
  name,
  description,
  billingModel,
  maxDocumentsPerMonth,
  monthlyCredits,
  canExceedLimit,
  prices,
  isCurrentPlan,
  action
}: PlanCardProps) {
  const t = useFeatureTranslation('systemSettings.payments');
  const { i18n } = useTranslation();

  const price = prices.find(p => p.interval === 'MONTH');
  const isCreditPlan = billingModel === BillingModel.CREDITS;
  const credits = monthlyCredits ?? 0;

  return (
    <Card
      className={`h-full overflow-hidden ${
        isCurrentPlan
          ? 'border-primary-base bg-primary-base/5 ring-1 ring-primary-base/30'
          : 'hover:scale-[1.01] hover:border-border-input'
      }`}
    >
      <CardHeader>
        <div className='flex items-start justify-between gap-sm'>
          <CardTitle
            className={`text-base font-bold ${isCurrentPlan ? 'text-primary-base' : 'text-foreground-base'}`}
          >
            {name}
          </CardTitle>
          {isCurrentPlan && (
            <span className='shrink-0 rounded-full bg-primary-base px-sm py-2xs text-xs font-medium text-white'>
              {t('planCard.currentPlan')}
            </span>
          )}
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>

      <CardContent className='flex flex-col gap-xs flex-1'>
        {isCreditPlan ? (
          <div className='flex items-center gap-xs text-sm text-foreground-muted'>
            <Icon name='coins' className='h-4 w-4 shrink-0' />
            <span>
              <strong className='text-foreground-base'>
                {credits.toLocaleString(i18n.language)}
              </strong>{' '}
              {t('planCard.creditsPerMonth', { count: credits })}
            </span>
          </div>
        ) : (
          <div className='flex items-center gap-xs text-sm text-foreground-muted'>
            <Icon name='file-lines' className='h-4 w-4 shrink-0' />
            <span>
              <strong className='text-foreground-base'>
                {maxDocumentsPerMonth.toLocaleString(i18n.language)}
              </strong>{' '}
              {t('planCard.documentsPerMonth')}
            </span>
          </div>
        )}

        {!isCreditPlan && canExceedLimit && (
          <div className='flex items-center gap-xs text-sm text-foreground-muted'>
            <Icon name='circle-check' className='h-4 w-4 shrink-0 text-success-text' />
            <span>{t('planCard.overageAllowed')}</span>
          </div>
        )}
      </CardContent>

      <CardFooter className='flex flex-col gap-sm pt-0'>
        <div
          className={`w-full border-t ${isCurrentPlan ? 'border-primary-base/20' : 'border-border-base'}`}
        />

        {price ? (
          <div className='flex items-baseline gap-xs self-start mt-sm'>
            <span
              className={`text-2xl font-bold ${isCurrentPlan ? 'text-primary-base' : 'text-foreground-base'}`}
            >
              {price.amount.toNumber().toLocaleString(i18n.language)} {price.currency}
            </span>
            <span className='text-sm text-foreground-muted'>{t('planCard.perMonth')}</span>
          </div>
        ) : (
          <span className='text-sm text-foreground-muted self-start mt-sm'>
            {t('planCard.noPriceForInterval')}
          </span>
        )}

        <PlanCardActionButton action={action} hasPrice={!!price} />
      </CardFooter>
    </Card>
  );
}

function PlanCardActionButton({ action, hasPrice }: { action: PlanCardAction; hasPrice: boolean }) {
  const t = useFeatureTranslation('systemSettings.payments');

  if (action.type === 'currentPlan') {
    return (
      <div className='flex w-full items-center justify-center gap-xs rounded-md border border-primary-base/40 bg-primary-base/10 py-sm text-sm font-medium text-primary-base mt-xs'>
        <Icon name='check' className='h-3.5 w-3.5' />
        {t('planCard.currentPlan')}
      </div>
    );
  }

  if (action.type === 'cancelScheduledChange') {
    return (
      <Button
        variant='default'
        outline='default'
        size='sm'
        className='w-full mt-xs'
        disabled={action.loading}
        onClick={action.onClick}
      >
        {action.loading ? t('changePlan.processing') : t('planCard.cancelScheduledChange')}
      </Button>
    );
  }

  if (action.type === 'cancelSubscription') {
    return (
      <Button
        variant='default'
        outline='default'
        size='sm'
        className='w-full mt-xs'
        disabled={action.loading}
        onClick={action.onClick}
      >
        {action.loading ? t('cancelSubscription.processing') : t('planCard.cancelSubscription')}
      </Button>
    );
  }

  if (action.type === 'upgrade') {
    return (
      <Button
        variant='primary'
        size='sm'
        className='w-full mt-xs'
        disabled={action.loading}
        onClick={action.onClick}
      >
        {action.loading ? (
          <span className='flex items-center justify-center gap-xs'>
            <Loader size='sm' />
            {t('changePlan.processing')}
          </span>
        ) : (
          t('planCard.upgrade')
        )}
      </Button>
    );
  }

  if (action.type === 'scheduleDowngrade') {
    return (
      <Button
        variant='default'
        outline='default'
        size='sm'
        className='w-full mt-xs'
        disabled={action.loading}
        onClick={action.onClick}
      >
        {action.loading ? t('changePlan.processing') : t('planCard.scheduleDowngrade')}
      </Button>
    );
  }

  if (action.type === 'checkout') {
    return (
      <Button
        variant='primary'
        size='sm'
        className='w-full mt-xs'
        disabled={action.loading || !hasPrice}
        onClick={action.onClick}
      >
        {action.loading ? (
          <span className='flex items-center justify-center gap-xs'>
            <Loader size='sm' />
            {t('planCard.upgrading')}
          </span>
        ) : (
          t('planCard.selectPlan')
        )}
      </Button>
    );
  }

  // disabled
  return (
    <Button variant='default' outline='default' size='sm' className='w-full mt-xs' disabled>
      {t('planCard.unavailable')}
    </Button>
  );
}
